import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from 'components/ui/Layout';
import { useCmsPages } from 'features/cms/hooks/useCms';
import { usePrefetchCms } from 'features/cms/hooks/usePrefetchCms';
import { useURLSync } from 'features/cms/hooks/useURLSync';
import { DeleteCmsModal } from 'features/cms/components/DeleteCmsModal';
import { useUserProfile } from 'features/auth/hooks/useUserProfile';

import {
  PAGE_TYPE_OPTIONS,
  STATUS_OPTIONS,
} from 'features/cms/types/cms.types';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Edit,
  Eye,
  FileText,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import { formatDateTime } from 'utils/dateFormatter';

const CmsManagement = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useURLSync({
    page: '1',
    limit: '10',
    status: 'all',
    pageType: 'all',
    search: '',
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  const [searchInput, setSearchInput] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedCmsPage, setSelectedCmsPage] = useState(null);

  // Fetch CMS pages
  const { data, isPending, isError, error } = useCmsPages(filters);

   const { data: profileData } = useUserProfile();
      const user = profileData?.data;
      
      const restrictedRoles = [
        "delivery_manager",
        "travel_manager",
        "operations_manager",
        "category_manager",
        "finance_manager",
      ];
      
      const showPermissionError = restrictedRoles.includes(
        user?.role?.name
      );

  // Prefetch
  const { prefetchNextPage, prefetchPrevPage } = usePrefetchCms();

  // Prefetch adjacent pages
  useEffect(() => {
    if (data?.pagination) {
      const { currentPage, totalPages } = data.pagination;
      prefetchNextPage(filters, totalPages);
      if (currentPage > 1) {
        prefetchPrevPage(filters);
      }
    }
  }, [data, filters]);

  // Update filter and reset to page 1
  const updateFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: key === 'page' ? value : '1',
    }));
  };

  // Search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        updateFilter('search', searchInput);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setSearchInput(filters.search);
  }, [filters.search]);

  // Sort handler
  const handleSort = (column) => {
    const newOrder =
      filters.sortBy === column && filters.sortOrder === 'asc' ? 'desc' : 'asc';
    setFilters((prev) => ({
      ...prev,
      sortBy: column,
      sortOrder: newOrder,
    }));
  };

  // Export handler
  const handleExport = () => {
    // console.log('Exporting CMS data with filters:', filters);
  };

  const SortIcon = ({ column }) => {
    if (filters.sortBy !== column)
      return <span className='text-muted-foreground'>↕</span>;
    return filters.sortOrder === 'asc' ? <span>↑</span> : <span>↓</span>;
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'published':
        return 'bg-green-900/30 text-green-400 border-green-700';
      case 'draft':
        return 'bg-yellow-900/30 text-yellow-100 border-yellow-700';
      case 'archived':
        return 'bg-gray-700/30 text-muted-foreground border-gray-600';
      default:
        return 'bg-gray-700/30 text-muted-foreground border-gray-600';
    }
  };

  // Get page type label
  const getPageTypeLabel = (pageType) => {
    const option = PAGE_TYPE_OPTIONS.find((opt) => opt.value === pageType);
    return option ? option.label : pageType;
  };

  // Pagination helpers
  const currentPage = parseInt(filters.page);
  const totalPages = data?.pagination?.totalPages || 1;

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  return (
    <Layout>
      <div className="min-h-screen bg-background text-foreground p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 space-y-4 sm:space-y-0">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-1">
                CMS Management
              </h3>
              <p className="text-sm text-muted-foreground">
                Manage your content pages (Terms, Privacy Policy, About Us,
                etc.)
              </p>
            </div>
            {!showPermissionError && (
              <button
                onClick={() => navigate("/cms/add")}
                className="flex items-center space-x-2 px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
              >
                <FileText size={16} />
                <span>Add CMS Page</span>
              </button>
            )}
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            {/* Search */}
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                size={18}
              />
              <input
                type="text"
                placeholder="Search by title or slug..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-10 pr-10 py-2 bg-input border border-border rounded-md text-foreground placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              {searchInput && (
                <button
                  onClick={() => setSearchInput("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X size={18} />
                </button>
              )}
            </div>

            {/* Status Filter */}
            <select
              value={filters.status}
              onChange={(e) => updateFilter("status", e.target.value)}
              className="px-3 py-2 bg-input border border-border rounded-md text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            {/* Page Type Filter */}

            {/* Export Button */}
            {/* <button
              onClick={handleExport}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-input border border-border hover:bg-accent text-foreground rounded-md transition-colors"
            >
              <Download size={18} />
              Export
            </button> */}
          </div>

          {/* Stats */}
          {showPermissionError ? (
            <tr>
              <td
                colSpan="11"
                className="px-4 py-8 text-center text-destructive"
              ></td>
            </tr>
          ) : (
            data && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-card border border-border rounded-lg p-4 item-box">
                  <p className="text-sm text-muted-foreground">Total Pages</p>
                  <p className="text-2xl font-bold text-foreground mt-1">
                    {data.pagination?.totalItems || 0}
                  </p>
                </div>
                <div className="bg-card border border-green-900/30 rounded-lg p-4 item-box">
                  <p className="text-sm text-muted-foreground">Published</p>
                  <p className="text-2xl font-bold text-green-400 mt-1">
                    {data.data?.filter((page) => page.status === "published")
                      .length || 0}
                  </p>
                </div>
                <div className="bg-card border border-yellow-900/30 rounded-lg p-4 item-box">
                  <p className="text-sm text-muted-foreground">Drafts</p>
                  <p className="text-2xl font-bold text-yellow-400 mt-1">
                    {data.data?.filter((page) => page.status === "draft")
                      .length || 0}
                  </p>
                </div>
                <div className="bg-card border border-border rounded-lg p-4 item-box">
                  <p className="text-sm text-muted-foreground">Archived</p>
                  <p className="text-2xl font-bold text-muted-foreground mt-1">
                    {data.data?.filter((page) => page.status === "archived")
                      .length || 0}
                  </p>
                </div>
              </div>
            )
          )}

          {/* Table */}
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            {isPending ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : isError ? (
              <div className="flex items-center justify-center h-64 text-red-400">
                <p>Error: {error?.message || "Failed to load CMS pages"}</p>
              </div>
            ) : !data?.data || data.data.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                <FileText size={48} className="mb-4 opacity-50" />
                <p className="text-lg">No CMS pages found</p>
                <p className="text-sm mt-2">
                  Create your first page to get started
                </p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-input/50 border-b border-border">
                      <tr>
                        <th
                          onClick={() => handleSort("title")}
                          className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                        >
                          <div className="flex items-center gap-2">
                            Title <SortIcon column="title" />
                          </div>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Page Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Slug
                        </th>
                        <th
                          onClick={() => handleSort("status")}
                          className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                        >
                          <div className="flex items-center gap-2">
                            Status <SortIcon column="status" />
                          </div>
                        </th>
                        <th
                          onClick={() => handleSort("updatedAt")}
                          className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                        >
                          <div className="flex items-center gap-2">
                            Last Updated <SortIcon column="updatedAt" />
                          </div>
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {showPermissionError ? (
                        <tr>
                          <td
                            colSpan="11"
                            className="px-4 py-8 text-center text-destructive"
                          >
                            Error loading CMS: You do not have permission to
                            perform this action!
                          </td>
                        </tr>
                      ) : (
                        data.data.map((page) => (
                          <tr
                            key={page._id}
                            className="hover:bg-input/50 transition-colors"
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-foreground">
                                {page.title?.english || "Untitled"}
                              </div>
                              {page.title?.hindi && (
                                <div className="text-xs text-muted-foreground mt-1">
                                  {page.title.hindi}
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm text-card-foreground">
                                {getPageTypeLabel(page.pageType)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm text-primary">
                                {page.slug}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex px-2 py-1 text-xs font-medium rounded-md border ${getStatusColor(
                                  page.status,
                                )}`}
                              >
                                {page.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                              {formatDateTime(page.updatedAt)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() =>
                                    navigate(`/cms/view/${page._id}`)
                                  }
                                  className="p-2 text-muted-foreground hover:text-primary transition-colors"
                                  title="View"
                                >
                                  <Eye size={18} />
                                </button>
                                <button
                                  onClick={() =>
                                    navigate(`/cms/edit/${page._id}`)
                                  }
                                  className="p-2 text-muted-foreground hover:text-primary transition-colors"
                                  title="Edit"
                                >
                                  <Edit size={18} />
                                </button>
                                <button
                                onClick={() => {
                                  setSelectedCmsPage(page);
                                  setIsDeleteModalOpen(true);
                                }}
                                className='p-2 text-muted-foreground hover:text-red-400 hover:bg-red-900/20 rounded transition-colors'
                                title='Delete'
                              >
                                <Trash2 size={18} />
                              </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {!showPermissionError && totalPages > 1 && (
                  <div className="flex items-center justify-between px-6 py-4 border-t border-border">
                    <div className="text-sm text-muted-foreground">
                      Showing {(currentPage - 1) * parseInt(filters.limit) + 1}{" "}
                      to{" "}
                      {Math.min(
                        currentPage * parseInt(filters.limit),
                        data.pagination?.totalItems || 0,
                      )}{" "}
                      of {data.pagination?.totalItems || 0} results
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() =>
                          updateFilter("page", String(currentPage - 1))
                        }
                        disabled={currentPage === 1}
                        className="p-2 rounded-md bg-input border border-border text-muted-foreground hover:text-foreground hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronLeft size={18} />
                      </button>

                      {getPageNumbers().map((pageNum, idx) =>
                        pageNum === "..." ? (
                          <span
                            key={`ellipsis-${idx}`}
                            className="px-3 py-1 text-muted-foreground"
                          >
                            ...
                          </span>
                        ) : (
                          <button
                            key={pageNum}
                            onClick={() =>
                              updateFilter("page", String(pageNum))
                            }
                            className={`px-3 py-1 rounded-md transition-colors  ${
                              currentPage === pageNum
                                ? "bg-primary text-foreground text-white"
                                : "bg-input border border-border text-muted-foreground hover:text-foreground hover:bg-accent"
                            }`}
                          >
                            {pageNum}
                          </button>
                        ),
                      )}

                      <button
                        onClick={() =>
                          updateFilter("page", String(currentPage + 1))
                        }
                        disabled={currentPage === totalPages}
                        className="p-2 rounded-md bg-input border border-border text-muted-foreground hover:text-foreground hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronRight size={18} />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      <DeleteCmsModal
        isDeleteModalOpen={isDeleteModalOpen}
        setIsDeleteModalOpen={setIsDeleteModalOpen}
        cmsPage={selectedCmsPage}
      />
    </Layout>
  );
};

export default CmsManagement;
